import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  Circle as CircleIcon,
} from "@mui/icons-material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import { useRtl } from "@ryde/redux";
import {
  ComparingEnum,
  currencySignSelector,
  DashboardCardNameEnum,
  DashboardCardTemplateTypeEnum,
  DATA_E2E_KEY,
  IStyledAttributes,
  percentFormula,
  pickNumericCardSubheaderColor,
  useFormats,
} from "@ryde/shared";
import { rydeColors, rydeColorsDashboardPage } from "@ryde/theme";

const IconContainer = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "icon-dashboard-container",
})`
  margin-inline-start: 3px;
`;

const ValueContainer = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "value-dashboard-container",
})<{ $colorCalc: string }>`
  font-weight: bold;
  margin-inline-start: 3px;
  ${({ $colorCalc }) => {
    return { color: $colorCalc };
  }};
`;

const SubtitleContainer = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "subtitle-dashboard-container",
})<{ $colorCalc: string }>`
  margin-inline-start: 3px;
  ${({ $colorCalc }) => {
    return { color: $colorCalc };
  }};
`;

const DayOfWeekContainer = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "day-of-week-dashboard-container",
})`
  color: ${rydeColorsDashboardPage.greyTextNumeric};
  margin-inline-start: 3px;
`;

export interface INumericComparisonContentProps {
  cardType: DashboardCardTemplateTypeEnum;
  cardName: DashboardCardNameEnum;
  content: INumericComparisonCardSubheaderContent;
  settings: INumericComparisonCardSubheaderSettings;
}
export interface INumericComparisonCardSubheaderContent {
  valueToday: number;
  valueLastWeek: number;
}

export interface INumericComparisonCardSubheaderSettings {
  maxDiffPercent: number;
  hasSubheader: boolean;
  predicate: (
    valueToday: number,
    valueLastWeek?: number,
    maxDiffPercent?: number,
  ) => ComparingEnum;
}
export const DashboardCardNumericComparisonContentHeaderStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "dashboard-numeric-comparison-content-header",
  },
)`
  background-color: ${rydeColors.white};
  border-bottom: 1px dashed
    ${rydeColorsDashboardPage.dashboardHeaderBorderColor};
  height: 15%;
  display: flex;
  align-items: center;
`;

export const CardNumericComparisonContentSubheaderTemplate: FC<
  INumericComparisonContentProps
> = ({
  cardName,
  cardType = DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON,
  content,
  settings,
}) => {
  const { t, i18n } = useTranslation();
  const {
    formatDayOfWeekTranslated,
    getSubheaderComparisonTranslated,
    getNumberWithCurrencyNormalized,
    getNumberWithCommasNormalized,
  } = useFormats();
  const { isRtl } = useRtl();
  const currencySign = useRecoilValue(currencySignSelector);

  const [subTitle, extension] = [
    t(`dashboard.${cardName}.SUBTITLE`),
    cardName === DashboardCardNameEnum.OVERALL_COST
      ? currencySign
      : t(`dashboard.${cardName}.EXTENSION`),
  ];

  const dayOfWeekT = formatDayOfWeekTranslated(i18n.language, {
    weekday: "long",
  });

  const lastDOW = getSubheaderComparisonTranslated(
    t("general.calendar.LAST"),
    dayOfWeekT,
    isRtl,
  );

  const predicateResult = settings.predicate(
    content.valueToday,
    content.valueLastWeek,
    settings.maxDiffPercent,
  );

  const colorCalc = pickNumericCardSubheaderColor(predicateResult);

  let percent = 0;

  if (content.valueLastWeek !== 0) {
    percent = percentFormula(content.valueToday, content.valueLastWeek);

    if (percent > 100) {
      percent -= 100;
    } else {
      percent = 100 - percent;
    }
  }

  const value = getNumberWithCommasNormalized(content.valueLastWeek);
  return (
    <DashboardCardNumericComparisonContentHeaderStyled>
      <IconContainer>
        {content.valueToday === content.valueLastWeek && (
          <CircleIcon fontSize={"inherit"} style={{ color: colorCalc }} />
        )}
        {content.valueToday > content.valueLastWeek && (
          <ArrowDropUpIcon fontSize={"medium"} style={{ color: colorCalc }} />
        )}
        {content.valueToday < content.valueLastWeek && (
          <ArrowDropDownIcon fontSize={"medium"} style={{ color: colorCalc }} />
        )}
      </IconContainer>
      <ValueContainer $colorCalc={colorCalc}>
        {getNumberWithCurrencyNormalized(value, extension)}
      </ValueContainer>
      <SubtitleContainer $colorCalc={colorCalc}>
        {`${subTitle} (${percent}%) `}
      </SubtitleContainer>
      <DayOfWeekContainer>{lastDOW}</DayOfWeekContainer>
    </DashboardCardNumericComparisonContentHeaderStyled>
  );
};
